import {
  Card,
  Collapse,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Modal,
  Rating,
  Skeleton,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
// Kaizen Dashboard components
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { getUproColor } from "utils/colors";
import dayjs from "dayjs";
import SoftInput from "components/SoftInput";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "40%", md: "40%" },
  maxHeight: "90vh",
  overflowY: "auto",
  p: 3,
};

const GET_VALORACIONES = gql`
  query valoracionesPlatos($filter: JSON) {
    valoracionesPlatos(filter: $filter) {
      id
      sabor
      cantidad
      comentario
      producto {
        id
        nombre
      }
      pedido {
        id
        fechaPedido
      }
    }
  }
`;

export default function ModalValoraciones({
  open,
  handleClose,
  idProducto,
  promedioTotal,
  producto,
}) {
  const [valoraciones, setValoraciones] = useState([]);
  const [desde, setDesde] = useState(null);
  const [hasta, setHasta] = useState(null);
  const [promedioFecha, setPromedioFecha] = useState(0);
  const [getValoraciones, { loading, data }] = useLazyQuery(GET_VALORACIONES, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.valoracionesPlatos) {
        setValoraciones(data.valoracionesPlatos);
      }
    },
  });

  useEffect(() => {
    if (open) {
      setValoraciones([]);
      setDesde(dayjs().format("YYYY-MM-DD"));
      setHasta(dayjs().format("YYYY-MM-DD"));
      getValoraciones({
        variables: {
          filter: {
            idProducto: idProducto,
            _pedido: {
              fechaPedido: {
                _between: [dayjs().format("YYYY-MM-DD"), dayjs().format("YYYY-MM-DD")],
              },
            },
          },
        },
      });
    }
  }, [open]);

  function calcularPromedioGeneral(valoraciones) {
    if (!Array.isArray(valoraciones) || valoraciones.length === 0) return 0;

    const sumaPromedios = valoraciones.reduce((acc, v) => {
      const sabor = v?.sabor ?? 0;
      const cantidad = v?.cantidad ?? 0;
      const promedio = (sabor + cantidad) / 2;
      return acc + promedio;
    }, 0);

    let promedioGeneral = sumaPromedios / valoraciones.length;

    // Redondear al múltiplo de 0.25 más cercano
    promedioGeneral = Math.round(promedioGeneral * 4) / 4;

    // Limitar entre 0.5 y 5
    promedioGeneral = Math.max(0.5, Math.min(5, promedioGeneral));

    return promedioGeneral;
  }

  useEffect(() => {
    if (valoraciones.length > 0) {
      const promedio = calcularPromedioGeneral(valoraciones);
      setPromedioFecha(promedio);
    }
  }, [valoraciones]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox pb={2} display="flex" justifyContent="space-between" alignItems="center">
            <SoftTypography variant="h6">{`Valoraciones de ${producto?.nombre}`}</SoftTypography>
            <SoftBox>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
          </SoftBox>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Card>
                <SoftBox p={2} display="flex" flexDirection="column" alignItems="center">
                  <Icon
                    fontSize="large"
                    sx={{
                      color: getUproColor("uproYellow"),
                      mb: 2,
                    }}
                  >
                    star
                  </Icon>
                  <SoftTypography variant="h5" mb={2}>
                    Valoración Total: {promedioTotal || "N/A"}
                  </SoftTypography>
                </SoftBox>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card>
                <SoftBox p={2} display="flex" flexDirection="column" alignItems="center">
                  <Icon
                    fontSize="large"
                    sx={{
                      color: getUproColor("uproYellow"),
                      mb: 2,
                    }}
                  >
                    calendar_today
                  </Icon>
                  <SoftTypography variant="h5" mb={2}>
                    Valoraciones por Fecha: {promedioFecha || "N/A"}
                  </SoftTypography>
                </SoftBox>
              </Card>
            </Grid>
            <Grid item xs={4} sx={{ mt: 2 }}>
              <InputLabel htmlFor="desde">
                <SoftTypography variant="caption" color="text">
                  Desde
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="desde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                error={desde && hasta && dayjs(desde).isAfter(hasta)}
                inputProps={{
                  max: hasta || dayjs().format("YYYY-MM-DD"),
                }}
              />
            </Grid>
            <Grid item xs={4} sx={{ mt: 2 }}>
              <InputLabel htmlFor="hasta">
                <SoftTypography variant="caption" color="text">
                  Hasta
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="hasta"
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                error={hasta && desde && dayjs(hasta).isBefore(desde)}
                inputProps={{
                  min: desde,
                  max: dayjs().format("YYYY-MM-DD"),
                }}
              />
            </Grid>
            <Grid item xs={4} sx={{ mt: 2 }}>
              <SoftButton
                color="primary"
                fullWidth
                sx={{ mt: 3.4 }}
                onClick={() => {
                  getValoraciones({
                    variables: {
                      filter: {
                        idProducto: idProducto,
                        _pedido: {
                          fechaPedido: {
                            _between: [desde, hasta],
                          },
                        },
                      },
                    },
                  });
                }}
                disabled={loading || !desde || !hasta}
              >
                {loading ? "Cargando..." : "Filtrar Valoraciones"}
              </SoftButton>
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Collapse in={valoraciones.length > 0 && !loading}>
                <Card>
                  <SoftBox
                    p={2}
                    sx={{
                      maxHeight: "50vh",
                      overflowY: "auto",
                    }}
                  >
                    {valoraciones.map((valoracion) => (
                      <SoftBox
                        key={valoracion.id}
                        mb={2}
                        p={2}
                        borderRadius="lg"
                        boxShadow="sm"
                        bgcolor="white"
                        display="flex"
                        flexDirection="column"
                      >
                        <SoftTypography variant="button" fontWeight="bold">
                          Producto: {valoracion.producto.nombre}
                        </SoftTypography>
                        <SoftTypography
                          variant="button"
                          fontWeight="bold"
                          display="flex"
                          alignItems="center"
                        >
                          Sabor:
                          <Rating
                            name="valoracion-sabor"
                            value={valoracion.sabor}
                            readOnly
                            size="small"
                            sx={{ ml: 1, color: getUproColor("uproYellow") }}
                          />
                          <SoftTypography
                            variant="caption"
                            color="textSecondary"
                            sx={{ mt: 0.5, ml: 1 }}
                          >
                            {valoracion.sabor} estrellas
                          </SoftTypography>
                        </SoftTypography>
                        <SoftTypography
                          variant="button"
                          fontWeight="bold"
                          display="flex"
                          alignItems="center"
                        >
                          Cantidad:
                          <Rating
                            name="valoracion-cantidad"
                            value={valoracion.cantidad}
                            readOnly
                            size="small"
                            sx={{ ml: 1, color: getUproColor("uproYellow") }}
                          />
                          <SoftTypography
                            variant="caption"
                            color="textSecondary"
                            sx={{ mt: 0.5, ml: 1 }}
                          >
                            {valoracion.cantidad} estrellas
                          </SoftTypography>
                        </SoftTypography>
                        <SoftTypography variant="button" fontWeight="bold">
                          Comentario: {valoracion.comentario || "Sin comentario"}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="textSecondary">
                          Pedido: {dayjs(valoracion.pedido.fechaPedido).format("DD/MM/YYYY")}
                        </SoftTypography>
                      </SoftBox>
                    ))}
                  </SoftBox>
                </Card>
              </Collapse>
              <Collapse in={loading}>
                <SoftBox p={2} textAlign="center">
                  <Icon
                    fontSize="large"
                    color="primary"
                    sx={{
                      animation: "shakes 1.5s infinite",
                      "@keyframes shakes": {
                        "0%": { transform: "rotate(0deg)" },
                        "25%": { transform: "rotate(10deg)" },
                        "50%": { transform: "rotate(-20deg)" },
                        "75%": { transform: "rotate(15deg)" },
                        "100%": { transform: "rotate(-5deg)" },
                      },
                    }}
                  >
                    search
                  </Icon>
                  <SoftTypography variant="h6" color="textSecondary">
                    Buscando valoraciones...
                  </SoftTypography>
                </SoftBox>
              </Collapse>
              <Collapse in={valoraciones.length === 0 && !loading}>
                <SoftBox p={2} textAlign="center">
                  <Icon
                    fontSize="large"
                    color="primary"
                    sx={{
                      animation: "shakes 1.5s",
                      "@keyframes shakes": {
                        "0%": { transform: "rotate(0deg)" },
                        "25%": { transform: "rotate(10deg)" },
                        "50%": { transform: "rotate(-20deg)" },
                        "75%": { transform: "rotate(15deg)" },
                        "100%": { transform: "rotate(-5deg)" },
                      },
                    }}
                  >
                    star_border
                  </Icon>
                  <SoftTypography variant="h6" color="textSecondary">
                    No se encontraron valoraciones para este producto en el rango de fechas
                    seleccionado.
                  </SoftTypography>
                </SoftBox>
              </Collapse>
            </Grid>
          </Grid>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalValoraciones.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  idProducto: PropTypes.string.isRequired,
  promedioTotal: PropTypes.number,
  producto: PropTypes.object,
};
